import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getUploadUrl } from '../../utils/apiUrl';
import './AddDistrict.css';

const EditDistrict = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    district_name: '',
    primary_color: '#3498db',
    secondary_color: '#2ecc71',
    primary_contact: '',
    contact_phone: '',
    contact_email: '',
    address: '',
    address_line_2: '',
    city: '',
    state: '',
    zip: '',
    school_website: '',
    subdomain: ''
  });
  const [logo, setLogo] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingCSV, setUploadingCSV] = useState(false);
  const [csvUploadResult, setCsvUploadResult] = useState(null);
  const navigate = useNavigate();

  const fetchDistrict = useCallback(async () => {
    try {
      const response = await axios.get(`/api/districts/${id}`);
      setFormData(response.data);
    } catch (error) {
      console.error('Failed to fetch district:', error);
      setError('Failed to load district');
    }
  }, [id]);

  useEffect(() => {
    fetchDistrict();
  }, [fetchDistrict]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    setLogo(e.target.files[0]);
  };

  const handleCSVFileChange = (e) => {
    setCsvFile(e.target.files[0]);
    setCsvUploadResult(null);
  };

  const handleCSVUpload = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      setError('Please select a CSV file');
      return;
    }

    setUploadingCSV(true);
    setError('');
    setCsvUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('csvFile', csvFile);

      const response = await axios.post(`/api/returning-student-imports/upload/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setCsvUploadResult({
        success: true,
        message: response.data.message,
        studentCount: response.data.studentCount
      });
      setCsvFile(null);
      // Reset file input
      const fileInput = document.getElementById('csvFile');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      setCsvUploadResult({
        success: false,
        error: error.response?.data?.error || 'Failed to upload CSV file'
      });
    } finally {
      setUploadingCSV(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });
      if (logo) {
        formDataToSend.append('logo', logo);
      }

      await axios.put(`/api/districts/${id}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      navigate('/superadmin/districts');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update district');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-district">
      <h2>Edit District</h2>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label>District Name *</label>
            <input
              type="text"
              name="district_name"
              value={formData.district_name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Subdomain *</label>
            <input
              type="text"
              name="subdomain"
              value={formData.subdomain}
              onChange={handleChange}
              required
              pattern="[a-z0-9-]+"
              title="Lowercase letters, numbers, and hyphens only"
            />
            <small>.backtoschoolforms.com</small>
          </div>
        </div>

        <div className="form-group">
          <label>Logo (JPG, PNG, WEBP)</label>
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileChange}
          />
          {formData.logo_url && !logo && (
            <small>Current logo: <a href={getUploadUrl(formData.logo_url, formData.subdomain)} target="_blank" rel="noopener noreferrer">View</a></small>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Primary Color</label>
            <input
              type="color"
              name="primary_color"
              value={formData.primary_color}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Secondary Color</label>
            <input
              type="color"
              name="secondary_color"
              value={formData.secondary_color}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Primary Contact *</label>
            <input
              type="text"
              name="primary_contact"
              value={formData.primary_contact}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Contact Phone *</label>
            <input
              type="tel"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label>Contact Email *</label>
          <input
            type="email"
            name="contact_email"
            value={formData.contact_email}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Address *</label>
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Address Line 2</label>
          <input
            type="text"
            name="address_line_2"
            value={formData.address_line_2 || ''}
            onChange={handleChange}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>City *</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>State *</label>
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Zip *</label>
            <input
              type="text"
              name="zip"
              value={formData.zip}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label>School Website</label>
          <input
            type="url"
            name="school_website"
            value={formData.school_website || ''}
            onChange={handleChange}
          />
        </div>

        <div style={{ marginTop: '2rem', padding: '1.5rem', border: '1px solid #dee2e6', borderRadius: '6px', background: '#f8f9fa' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Returning Student CSV Import</h3>
          <p style={{ marginBottom: '1rem', color: '#6c757d' }}>
            Upload a CSV file containing currently enrolled students. This will allow parents to import student data 
            when filling out returning student enrollment forms. This is typically done once per year, per district.
          </p>
          
          <form onSubmit={handleCSVUpload} style={{ marginBottom: '1rem' }}>
            <div className="form-group">
              <label>CSV File</label>
              <input
                id="csvFile"
                type="file"
                accept=".csv"
                onChange={handleCSVFileChange}
                required
              />
              <small style={{ display: 'block', marginTop: '0.25rem', color: '#6c757d' }}>
                CSV should contain student data with columns: Student Legal Last Name, Student Federal ID Number, etc.
              </small>
            </div>
            <button 
              type="submit" 
              className="btn-secondary" 
              disabled={uploadingCSV || !csvFile}
              style={{ opacity: (uploadingCSV || !csvFile) ? 0.6 : 1, cursor: (uploadingCSV || !csvFile) ? 'not-allowed' : 'pointer' }}
            >
              {uploadingCSV ? 'Uploading...' : 'Upload CSV'}
            </button>
          </form>

          {csvUploadResult && (
            <div style={{
              padding: '0.75rem',
              marginTop: '1rem',
              backgroundColor: csvUploadResult.success ? '#d4edda' : '#f8d7da',
              border: `1px solid ${csvUploadResult.success ? '#c3e6cb' : '#f5c6cb'}`,
              borderRadius: '4px',
              color: csvUploadResult.success ? '#155724' : '#721c24'
            }}>
              {csvUploadResult.success ? (
                <div>
                  <strong>✅ Upload Successful:</strong> {csvUploadResult.message}
                  {csvUploadResult.studentCount !== undefined && (
                    <p style={{ margin: '0.5rem 0 0 0' }}>
                      Found {csvUploadResult.studentCount} student record(s) in the CSV.
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <strong>❌ Upload Failed:</strong> {csvUploadResult.error}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Updating...' : 'Update District'}
          </button>
          <button 
            type="button" 
            className="btn-secondary" 
            onClick={() => navigate('/superadmin/districts')}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditDistrict;

